// Compliance Trend Line — week-over-week compliance-violation rate on
// the executive dashboard. Green when trending down, red when trending
// up. Coaching effectiveness signal.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ShieldAlert, TrendingDown, TrendingUp, Minus, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIRECTION_META = {
  improving: {
    icon: TrendingDown,
    tone: 'text-emerald-600',
    bg: 'bg-emerald-50',
    label: 'Improving',
    verb: 'down',
  },
  worsening: {
    icon: TrendingUp,
    tone: 'text-rose-600',
    bg: 'bg-rose-50',
    label: 'Trending up',
    verb: 'up',
  },
  stable: {
    icon: Minus,
    tone: 'text-slate-600',
    bg: 'bg-slate-100',
    label: 'Stable',
    verb: 'flat',
  },
};

const TOOLTIP = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-2.5 shadow-lg text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      <p className="text-rose-600">
        Violation rate: <span className="font-semibold">{row.violation_rate}%</span>
      </p>
      <p className="text-slate-500 text-[11px]">
        {row.calls_with_violation} of {row.graded_calls} graded call{row.graded_calls === 1 ? '' : 's'}
      </p>
    </div>
  );
};

export const ComplianceTrendCard = ({ domainId = null, weeks = 12 }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const p = new URLSearchParams({ weeks: String(weeks) });
    if (domainId) p.set('domain_id', domainId);
    setLoading(true);
    axios
      .get(`${API}/analytics/compliance-trend?${p.toString()}`, { timeout: 20000 })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [domainId, weeks]);

  if (loading) {
    return (
      <Card className="border-slate-200 shadow-sm" data-testid="compliance-trend-loading">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const { weekly_series = [], totals = {}, delta_pct = 0, direction = 'stable', top_rules = [] } = data;
  const totalGraded = totals.graded_calls || 0;
  if (totalGraded === 0) {
    return (
      <Card className="border-slate-200 shadow-sm" data-testid="compliance-trend-empty">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-slate-400" />
            Compliance Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500">No graded calls in this window yet — trend will appear once activity picks up.</p>
        </CardContent>
      </Card>
    );
  }

  const meta = DIRECTION_META[direction] || DIRECTION_META.stable;
  const DirIcon = meta.icon;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="compliance-trend-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-rose-500" />
            Compliance Trend
            <span className="text-xs font-normal text-slate-400 ml-1">last {weeks} weeks</span>
          </CardTitle>
          <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.tone} ${meta.bg}`}
            data-testid="compliance-trend-direction"
          >
            <DirIcon className="w-3.5 h-3.5" />
            {meta.label}
            {delta_pct !== 0 && (
              <span className="ml-1 tabular-nums">
                {delta_pct > 0 ? '+' : ''}{delta_pct} pp
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <MiniStat label="Calls flagged" value={totals.calls_with_violation} tone="rose" />
          <MiniStat label="Rule trips" value={totals.violation_trips} tone="amber" />
          <MiniStat
            label="Overall rate"
            value={totalGraded > 0
              ? `${Math.round((totals.calls_with_violation / totalGraded) * 100 * 10) / 10}%`
              : '—'}
            tone="slate"
          />
        </div>

        <ResponsiveContainer width="100%" height={200}>
          <AreaChart
            data={weekly_series}
            margin={{ top: 5, right: 5, bottom: 5, left: -10 }}
            onClick={(state) => {
              // Click anywhere on the chart → jump to the calls list
              // filtered for governance-violation calls in that week.
              const w = state?.activeLabel;
              if (!w) return;
              const params = new URLSearchParams({ has_violation: '1', week: w });
              if (domainId) params.set('domain_id', domainId);
              navigate(`/calls?${params.toString()}`);
            }}
          >
            <defs>
              <linearGradient id="complianceGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="week" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} domain={[0, 'auto']} />
            <Tooltip content={<TOOLTIP />} />
            <Area
              type="monotone"
              dataKey="violation_rate"
              name="Violation rate"
              stroke="#f43f5e"
              fill="url(#complianceGrad)"
              strokeWidth={2}
              dot={{ r: 3, fill: '#f43f5e', cursor: 'pointer' }}
              activeDot={{ r: 6, cursor: 'pointer' }}
            />
          </AreaChart>
        </ResponsiveContainer>
        <p className="text-[11px] text-slate-400 mt-1 text-center">Click any week to see the flagged calls</p>

        {top_rules.length > 0 && (
          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">
              Most-tripped rules ({weeks}w)
            </p>
            <div className="space-y-1">
              {top_rules.slice(0, 3).map((r, i) => (
                <div
                  key={`${r.rule}-${i}`}
                  className="flex items-center justify-between bg-slate-50 rounded px-3 py-1.5"
                  data-testid={`compliance-top-rule-${i}`}
                >
                  <span className="text-xs text-slate-700 truncate flex-1 mr-2">{r.rule}</span>
                  <span className="text-xs text-rose-600 font-semibold tabular-nums">{r.count}×</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MiniStat = ({ label, value, tone }) => {
  const toneMap = {
    rose: 'text-rose-700 bg-rose-50',
    amber: 'text-amber-700 bg-amber-50',
    slate: 'text-slate-700 bg-slate-100',
  };
  return (
    <div className={`rounded-lg px-3 py-2 ${toneMap[tone] || 'bg-slate-50'}`}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className="text-xl font-bold tabular-nums">{value}</p>
    </div>
  );
};

export default ComplianceTrendCard;
